import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Globe } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";

const Header = () => {
  const { count, openCart } = useCart();
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const NAV = [
    { to: "/", label: t("nav.home"), key: "home" },
    { to: "/divisions", label: t("nav.universes"), key: "universes" },
    { to: "/sneakers", label: t("nav.sneakers"), key: "sneakers" },
    { to: "/perruques", label: t("nav.wigs"), key: "wigs" },
    { to: "/printing", label: t("nav.printing"), key: "printing" },
    { to: "/a-propos", label: t("nav.about"), key: "about" },
    { to: "/contact", label: t("nav.contact"), key: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const toggleLang = () => setLang(lang === "fr" ? "en" : "fr");

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          scrolled ? "backdrop-blur-2xl bg-[#050505]/80 border-b border-white/5" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
            <img
              src="https://customer-assets.emergentagent.com/job_sneaker-hub-242/artifacts/44serdge_Suzel%20gold.png"
              alt="Suzel Holding"
              className="h-16 md:h-20 w-auto transition-transform duration-500 group-hover:scale-105"
              data-testid="logo-image"
            />
            <span className="sr-only">Suzel Holding</span>
          </Link>

          <nav className="hidden xl:flex items-center gap-10">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.key}`}
                className={({ isActive }) =>
                  `font-grotesk text-[11px] uppercase tracking-[0.2em] transition-colors link-underline whitespace-nowrap ${
                    isActive ? "text-[#C5A059]" : "text-white/80 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 md:gap-4">
            <button
              data-testid="lang-toggle-button"
              onClick={toggleLang}
              className="hidden sm:flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-grotesk text-white/70 hover:text-[#C5A059] transition-colors px-2 py-1 border border-white/10 hover:border-[#C5A059]/40"
              aria-label="Change language"
              title={lang === "fr" ? "Switch to English" : "Passer en français"}
            >
              <Globe size={12} strokeWidth={1.6} />
              <span className={lang === "fr" ? "text-[#C5A059]" : ""}>FR</span>
              <span className="text-white/30">/</span>
              <span className={lang === "en" ? "text-[#C5A059]" : ""}>EN</span>
            </button>
            <button
              data-testid="cart-toggle-button"
              onClick={openCart}
              className="relative text-white hover:text-[#C5A059] transition-colors p-2"
              aria-label={t("nav.cart")}
            >
              <ShoppingBag size={20} strokeWidth={1.4} />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-0.5 -right-0.5 bg-[#C5A059] text-black text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              className="xl:hidden text-white p-2"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={t("nav.menu")}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="fixed inset-0 z-30 bg-[#050505] pt-24 px-6 xl:hidden overflow-y-auto"
        >
          <nav className="flex flex-col gap-2">
            {NAV.map((n, idx) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.key}`}
                className={({ isActive }) =>
                  `font-display text-3xl py-4 border-b border-white/5 ${
                    isActive ? "text-[#C5A059]" : "text-white"
                  }`
                }
                style={{ animation: `fade-up 0.6s ${idx * 0.06}s both` }}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-8 flex items-center gap-3 sm:hidden">
            <button
              onClick={toggleLang}
              data-testid="mobile-lang-toggle"
              className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-grotesk text-white/70 px-3 py-2 border border-white/10"
            >
              <Globe size={12} />
              <span className={lang === "fr" ? "text-[#C5A059]" : ""}>FR</span>
              <span className="text-white/30">/</span>
              <span className={lang === "en" ? "text-[#C5A059]" : ""}>EN</span>
            </button>
          </div>
          <div className="mt-10">
            <p className="overline mb-3">{t("nav.directContact")}</p>
            <a
              href="https://wa.me/243981962762"
              className="block text-white text-lg mb-2"
              data-testid="mobile-whatsapp-link"
            >
              +243 981 962 762
            </a>
            <a
              href="mailto:contact@suzelholding.com"
              className="block text-white/70 text-base"
              data-testid="mobile-email-link"
            >
              contact@suzelholding.com
            </a>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
